// Append a :not() filter to every rule's subject so AI-authored customCss can't
// style builder UI overlays that share the #page subtree (edit controls,
// +Section buttons, popovers). In non-builder contexts no element carries
// these markers, so the filter is a no-op.
//
// Two opt-out markers:
// - [data-builder-ui]      → element AND its descendants are excluded (chrome
//                            wrappers whose content is all builder UI).
// - [data-builder-ui-root] → element-only exclusion (container whose
//                            descendants include user-authored content via
//                            <slot/>, e.g. BuilderSection's outer wrapper).
//
// Parsing is delegated to postcss (rule/declaration boundaries) and
// postcss-selector-parser (selector structure). This keeps `{`/`}` that appear
// inside comments (`/* { } */`), strings (`content: "{"`), or urls
// (`url("…{…")`) from being mistaken for block delimiters — the failure mode of
// the previous hand-rolled brace scanner.
//
// The filter is inserted before any trailing pseudo-element (::before, ::after,
// ::placeholder, …) because pseudo-elements must be the last token in a
// selector — appending after them produces invalid CSS that browsers drop.
//
// Both parsers throw on malformed input, and customCss is free text a user can
// paste anything into. This runs inside a computed, so an escaping throw kills
// the whole page render — and because the settings dialog persists customCss
// before the re-render, the page stays broken across reloads with no way back
// through the UI. Browsers just skip CSS they can't parse; so does this.

import postcss from 'postcss';
import selectorParser from 'postcss-selector-parser';

const FILTER = ':not([data-builder-ui], [data-builder-ui] *, [data-builder-ui-root])';

// The only single-colon selectors that are pseudo-elements (CSS2 legacy form);
// every other `:foo` is a pseudo-class and composes fine after `:not()`.
const LEGACY_PSEUDO_ELEMENTS = new Set([':before', ':after', ':first-line', ':first-letter']);

const isPseudoElement = (node) => node.type === 'pseudo'
  && (node.value.startsWith('::') || LEGACY_PSEUDO_ELEMENTS.has(node.value.toLowerCase()));

// Frame rules inside @keyframes (0%, from, to) are not real selectors. Appending
// :not() to them produces invalid CSS, so the browser drops the whole animation —
// silently breaking any AI-authored `animation`. Detect the @keyframes wrapper
// (including vendor prefixes like -webkit-keyframes) so its frames are left alone.
const isKeyframesAtRule = (node) => node?.type === 'atrule' && /(^|-)keyframes$/i.test(node.name);

const transformSelectors = selectorParser((root) => {
  root.each((selector) => {
    const { nodes } = selector;

    // Walk back over the trailing run of pseudo-elements; the filter must land
    // just before them so they stay the last tokens in the selector.
    let firstTrailingPseudoElement = null;
    for (let i = nodes.length - 1; i >= 0; i--) {
      if (isPseudoElement(nodes[i])) {
        firstTrailingPseudoElement = nodes[i];
        continue;
      }
      break;
    }

    const filter = selectorParser().astSync(FILTER).first.first.clone();
    filter.spaces = { before: '', after: '' };

    if (firstTrailingPseudoElement) {
      selector.insertBefore(firstTrailingPseudoElement, filter);
    } else {
      // Attach the filter directly to the subject. Any trailing whitespace on the
      // last node (e.g. `h1 , h2`) must move to AFTER the filter, otherwise it
      // reads as a descendant combinator (`h1 :not(…)`) instead of a compound
      // (`h1:not(…)`) — a silent semantic change.
      const lastNode = nodes[nodes.length - 1];
      if (lastNode) {
        filter.spaces.after = lastNode.spaces.after;
        lastNode.spaces.after = '';
      }
      selector.append(filter);
    }
  });
});

export default function excludeBuilderUiFromCss(css) {
  if (!css) return css;

  let root;
  try {
    root = postcss.parse(css);
  } catch {
    // Nothing in the sheet is salvageable (unclosed block, stray prose). Return
    // empty rather than the raw input — unfiltered customCss would reach the
    // builder UI this function exists to protect.
    return '';
  }

  root.walkRules((rule) => {
    if (isKeyframesAtRule(rule.parent)) return;
    try {
      rule.selector = transformSelectors.processSync(rule.selector);
    } catch {
      // Only this selector is malformed; drop the rule and keep the rest of the
      // sheet working. Leaving it unfiltered would expose the builder UI.
      rule.remove();
    }
  });

  return root.toString();
}
